/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import axios from 'axios';
import moment from 'moment-timezone';
import urlJoin from 'proper-url-join';

import { endpoints } from '../../ia/util/config';
import { saveAs } from '../../ia/util/download';

const reportsEndpoint = assistantId =>
  urlJoin(
    endpoints.store,
    'v2',
    'tooling',
    'assistants',
    assistantId,
    'reports'
  );

const requestsEndpoint = skillId =>
  urlJoin(
    endpoints.store,
    'v2',
    'skills',
    skillId,
    'workspace',
    'recommended_intents'
  );

const requestGroupEndpoint = (group, skillId) =>
  urlJoin(
    endpoints.store,
    'v2',
    'skills',
    skillId,
    'workspace',
    'recommended_intents',
    group
  );

const setParams = (type, options) => {
  const params = {
    type: type,
    options: {
      language: options.language,
      assistant_id: options.assistantId
    }
  };

  //Add additional filter params if they are provided
  params.options = Object.assign(
    params.options,
    options.version ? { version: options.version } : null,
    options.startDate
      ? {
          start_time: options.startDate,
          end_time: options.endDate,
          interval: whichInterval(options.interval),
          time_zone: getTimezone()
        }
      : null,
    options.order ? { order: options.order } : null,
    options.conversationId ? { conversation_id: options.conversationId } : null,
    options.skillType ? { skill_type: options.skillType } : null,
    options.noIntents ? { intents_irrelevant: true } : null,
    options.intents ? { intents: options.intents } : null,
    options.keywords ? { log_keyword_filters: options.keywords } : null,
    options.containment
      ? { log_containment_filters: options.containment.queryText }
      : null,
    options.recognition
      ? { log_coverage_filters: options.recognition.queryText }
      : null,
    options.search ? { log_search_filters: options.search.queryText } : null,
    options.pageAfterKey ? { page_after_key: options.pageAfterKey } : null,
    options.sampleSize ? { sample_size: options.sampleSize } : null,
    options.topics ? { topics: options.topics } : null,
    options.completionStatus
      ? { completion_status: options.completionStatus }
      : null,
    options.filteredReasons
      ? { reason_filters: options.filteredReasons }
      : null,
    options.actionTitle ? { action_title: options.actionTitle } : null,
    options.recognitionType
      ? { recognition_type: options.recognitionType }
      : null,
    options.singleActionPagination
      ? { page_after_key: options.singleActionPagination }
      : null,
    options.sortField ? { sort_field: options.sortField } : null,
    options.conversationalSkillState
      ? { conversational_skill_state: options.conversationalSkillState }
      : null,
    // For convo search logs
    options.responseType ? { response_type: options.responseType } : null,
    options.resolution_method
      ? { resolution_method: options?.resolution_method }
      : null
  );

  if (
    type === 'messageLogs' ||
    type === 'conversationalSearchLogs' ||
    type === 'generativeAIMetrics'
  ) {
    delete params.options.interval;
    delete params.options.conversational_skill_state;
    params.options = {
      ...params.options,
      entities: options?.entities ?? null
    };
  }
  if (type === 'conversationalSearchLogs' || type === 'generativeAIMetrics') {
    // Probably need to fix this for all logs and logs since topics is [] and does not get null for that on the check above
    delete params.options.topics;
    delete params.options.entities;
  }
  return params;
};

const getTimezone = () => {
  return moment.tz(moment.tz.guess()).format('Z');
};

const whichInterval = interval => {
  let intervalParam = '';

  switch (interval) {
    case 'Hour':
      intervalParam = '1h';
      break;
    case 'Week':
      intervalParam = '1w';
      break;
    case 'Month':
      intervalParam = '1M';
      break;
    case 'Day':
    default:
      intervalParam = '1d';
      break;
  }

  return intervalParam;
};

export const getReports = (type, filterParams) => {
  const params = setParams(type, filterParams);

  return axios
    .post(reportsEndpoint(filterParams.parentAssistantId), params)
    .then(response => response.data.reports);
};

export const getUnrecognizedRequests = params => {
  const { skillId, integrationId, isRefresh, skill_reference_id } = params;
  const url = requestsEndpoint(skillId);
  return axios.get(url, {
    params: {
      integration_id: integrationId,
      skill_reference_id,
      model: 'latest',
      ...(isRefresh && {
        refresh: true
      })
    }
  });
};

export const downloadUnrecognizedGroups = params => {
  const { skillId, integrationId, skill_reference_id } = params;
  const url = requestsEndpoint(skillId);

  return axios
    .get(url, {
      headers: {
        accept: 'text/csv',
        'content-type': 'text/csv'
      },
      params: {
        integration_id: integrationId,
        skill_reference_id,
        page_limit: 50,
        model: 'latest',
        export: 'true'
      }
    })
    .then(response => {
      const filename = `${skillId}.csv`;
      const file = new Blob([response.data], {
        type: 'text/csv; charset=UTF-8'
      });
      if (navigator.msSaveBlob) {
        navigator.msSaveBlob(file, filename);
      } else {
        saveAs(URL.createObjectURL(file), filename);
      }
    });
};

export const getRequestsForGroup = params => {
  const { group, integrationId, skillId, skill_reference_id } = params;
  const url = requestGroupEndpoint(group, skillId);

  return axios.get(url, {
    params: {
      integration_id: integrationId,
      skill_reference_id
    }
  });
};

export const downloadUnrecognizedGroup = params => {
  const { group, integrationId, skillId, skill_reference_id } = params;
  const url = requestGroupEndpoint(group, skillId);

  return axios
    .get(url, {
      headers: {
        accept: 'text/csv',
        'content-type': 'text/csv'
      },
      params: {
        integration_id: integrationId,
        skill_reference_id,
        export: 'true'
      }
    })
    .then(response => {
      const filename = `${skillId}-single-group.csv`;
      const file = new Blob([response.data], {
        type: 'text/csv; charset=UTF-8'
      });
      if (navigator.msSaveBlob) {
        navigator.msSaveBlob(file, filename);
      } else {
        saveAs(URL.createObjectURL(file), filename);
      }
    });
};
